
import { z } from 'zod';
import api from '../axiosConfig';
const BASE_URL = '/verification/account-verification-service';
const IdNoValidator = (value: string) => {
    // if there is no other character except 0, then return false
    if (value.match(/^[0]+$/)) {
        return false;
    }
    return true;
};

// Define validation schema using Zod v4
const baseSchema = z.object({
    entityType: z.string({ error: 'Entity Type is required' }).min(1, 'Entity Type is required'),
    accountNumber: z.string({ error: 'Account Number is required' }).min(1, 'Account Number is required').max(13, 'Account Number cannot be more than 13 digits'),
    branchCode: z.string({ error: 'Branch Code is required' }).min(4, 'Branch Code should be 4 - 6 characters').max(6, 'Branch Code should be 4 - 6 characters'),
    accountType: z.string({ error: 'Account Type is required' }).min(1, 'Account Type is required'),
    bankName: z.string({ error: 'Bank Name is required' }).min(1, 'Bank Name is required'),
    voucherCode: z.string().optional(),
    clientReference: z.string().optional(),
});

const individualSchema = baseSchema.extend({
    verificationType: z.literal('Individual'),
    initials: z.string({ error: 'Initials are required' }).min(1, 'Initials are required').max(3, 'Initials cannot be more than 3 characters'),
    surname: z.string({ error: 'Surname is required' }).min(1, 'Surname is required').max(60, 'Surname cannot be more than 50 characters'),
    idNo: z.string({ error: 'ID Number is required' })
        .min(1, 'ID Number is required')
        .refine(IdNoValidator, 'ID Number cannot be 0'),
    idType: z.string({ error: 'ID Type is required' }).min(1, 'ID Type is required'),
    // Company fields must be optional here to allow switching types without validation errors on hidden fields
    companyName: z.string().optional(),
    reg1: z.string().length(4, "Reg1 must be 4 characters").optional(),
    reg2: z.string().length(6, "Reg2 must be 6 characters").optional(),
    reg3: z.string().length(2, "Reg3 must be 2 characters").optional(),
    trustNumber: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.idType === 'SID') {
        if (data.idNo.length !== 13) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'ID Number must be exactly 13 characters for SID',
                path: ['idNo'],
            });
        }
    }
});

const companySchema = baseSchema.extend({
    verificationType: z.literal('Company'),
    companyName: z.string({ error: 'Company Name is required' })
        .min(3, 'Company Name should be at least 3 characters')
        .max(60, 'Company Name should not be more than 60 characters'),
    reg1: z.string({ error: 'Reg1 is required' }).length(4, "Reg1 must be 4 characters"),
    reg2: z.string({ error: 'Reg2 is required' }).length(6, "Reg2 must be 6 characters"),
    reg3: z.string({ error: 'Reg3 is required' }).length(2, "Reg3 must be 2 characters"),
    trustNumber: z.string().optional(),
    // Individual fields must be optional here
    initials: z.string().optional(),
    surname: z.string().optional(),
    idNo: z.string().optional(),
    idType: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.entityType === 'Trust' && !data.trustNumber) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Trust Number is required for Trust entity',
            path: ['trustNumber'],
        });
    }
});

export const avsSchema = z.discriminatedUnion('verificationType', [
    individualSchema,
    companySchema,
]);

export type AccountVerificationPayload = z.infer<typeof avsSchema>;



export const accountVerificationService = {
    createPayload: (formData: z.infer<typeof avsSchema>): AccountVerificationPayload => {
        return {
            ...formData,
        };
    },

    submitAVS: async (payload: AccountVerificationPayload) => {
        try {
            console.log('Submitting AVS Payload:', payload);
            const response = await api.post(BASE_URL, payload);
            return response.data;
        } catch (error) {
            console.error('Error submitting AVS:', error);
            throw error;
        }
    }
};
